import React, { useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  NAUI_EXAM_PASS_PERCENT,
  NAUI_FREEDIVER_SKILLS,
  NAUI_FREEDIVER_TARGETS,
  NAUI_GRADUATION_REQUIREMENTS,
  NAUI_PREREQUISITES,
  POOL_ONLY_NOTE,
  SKILLS_DISCLAIMER,
  SkillEnvironment,
  skillGroups,
  skillsByEnvironment,
} from '../../data/nauiSkills';
import { formatSeconds } from '../../utils/apneaTables';

/**
 * NAUI Freediver course skill checklist.
 *
 * Self-assessment only — a NAUI instructor signs these off, not an app. The
 * disclaimer says so plainly, and the dry-trainable subset is marked so the diver
 * can see which items this app actually helps with versus which need water and an
 * instructor.
 */

const ENVIRONMENTS: Array<{ value: SkillEnvironment; label: string }> = [
  { value: 'knowledge', label: 'Knowledge' },
  { value: 'confined', label: 'Pool' },
  { value: 'openWater', label: 'Open water' },
];

interface SkillsChecklistProps {
  completed: Record<string, boolean>;
  saving?: boolean;
  onToggle: (skillId: string, done: boolean) => void;
}

const SkillsChecklist: React.FC<SkillsChecklistProps> = ({
  completed,
  saving = false,
  onToggle,
}) => {
  const [env, setEnv] = useState<SkillEnvironment>('knowledge');

  const total = NAUI_FREEDIVER_SKILLS.length;
  const doneCount = NAUI_FREEDIVER_SKILLS.filter(s => completed[s.id]).length;
  const skills = skillsByEnvironment(env);
  const groups = skillGroups(env);

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}
      >
        <Typography variant="subtitle1">NAUI Freediver course skills</Typography>
        <Chip size="small" label={`${doneCount} of ${total}`} />
      </Stack>

      <LinearProgress
        variant="determinate"
        value={(doneCount / total) * 100}
        sx={{ height: 6, borderRadius: 3, mb: 2 }}
      />

      <Alert severity="info" sx={{ mb: 2 }}>{SKILLS_DISCLAIMER}</Alert>

      {/* Course targets — the numbers worth training toward. */}
      <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
        <Chip
          size="small"
          variant="outlined"
          label={`depth ${NAUI_FREEDIVER_TARGETS.maxDepthMeters} m / ${NAUI_FREEDIVER_TARGETS.maxDepthFeet} ft`}
        />
        <Chip
          size="small"
          variant="outlined"
          color="primary"
          label={`static ${formatSeconds(NAUI_FREEDIVER_TARGETS.minStaticSeconds)}`}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`dynamic ${NAUI_FREEDIVER_TARGETS.minDynamicMeters} m`}
        />
        <Chip size="small" variant="outlined" label={`exam ${NAUI_EXAM_PASS_PERCENT}%`} />
      </Stack>

      <Tabs
        value={env}
        onChange={(_e, v) => setEnv(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        {ENVIRONMENTS.map(e => (
          <Tab key={e.value} value={e.value} label={e.label} />
        ))}
      </Tabs>

      {groups.map(group => (
        <Box key={group} sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {group}
          </Typography>
          <List dense disablePadding>
            {skills.filter(s => s.group === group).map(skill => (
              <ListItem key={skill.id} disableGutters sx={{ py: 0 }}>
                <FormControlLabel
                  sx={{ alignItems: 'flex-start', m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(completed[skill.id])}
                      disabled={saving}
                      onChange={e => onToggle(skill.id, e.target.checked)}
                    />
                  }
                  label={
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                      <Typography variant="body2">{skill.label}</Typography>
                      {skill.dryTrainable && (
                        <Chip size="small" label="dry-trainable" variant="outlined" />
                      )}
                    </Stack>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      ))}

      {env === 'openWater' && (
        <Alert severity="info" sx={{ mt: 1 }}>{POOL_ONLY_NOTE}</Alert>
      )}

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>Prerequisites</Typography>
      <List dense disablePadding>
        {NAUI_PREREQUISITES.map(p => (
          <ListItem key={p} disableGutters sx={{ py: 0 }}>
            <ListItemText primary={p} primaryTypographyProps={{ variant: 'body2' }} />
          </ListItem>
        ))}
      </List>

      <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
        To earn the certification
      </Typography>
      <List dense disablePadding>
        {NAUI_GRADUATION_REQUIREMENTS.map(r => (
          <ListItem key={r} disableGutters sx={{ py: 0 }}>
            <ListItemText primary={r} primaryTypographyProps={{ variant: 'body2' }} />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};

export default SkillsChecklist;
